import { makeAutoObservable } from "mobx";
import { toast } from "sonner";
import type { MapRef } from "react-map-gl/mapbox";
import type { RefObject } from "react";
import type { GeoJSONSourceSpecification, LineLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { MapBoxSource } from "@/components/Map/stores/MapBoxSource";
import { MapBoxLayer } from "@/components/Map/stores/MapBoxLayer";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { networkConnection } from "@/endpoints/dryp/networkConnection";
import type { LngLatTuple } from "@/components/Map/stores/MarkersStore";
import type { MapPopupsStore } from "@/components/Map/MapPopups/MapPopupsStore";
import { ConnectionPreviewStore } from "./ConnectionPreviewStore";
import { MissingConnectivityLayers } from "./MissingConnectivityLayers";

const LINE_ID = "network-connections-line";
const ARROW_ID = "network-connections-arrow";
const SOURCE_ID = "network-connections";
const BELOW_LAYER_ID = "markers-layer";
const THEME_COLOR = "#003BFF";

const emptyFC = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });

const DASH_SEQUENCE: number[][] = [
	[0, 4, 3],
	[0.5, 4, 2.5],
	[1, 4, 2],
	[1.5, 4, 1.5],
	[2, 4, 1],
	[2.5, 4, 0.5],
	[3, 4, 0],
	[0, 0.5, 3, 3.5],
	[0, 1, 3, 3],
	[0, 1.5, 3, 2.5],
	[0, 2, 3, 2],
	[0, 2.5, 3, 1.5],
	[0, 3, 3, 1],
	[0, 3.5, 3, 0.5],
];

const sourceSpec: GeoJSONSourceSpecification = { type: "geojson", data: emptyFC() };

const lineSpec: LineLayerSpecification = {
	id: LINE_ID,
	type: "line",
	source: SOURCE_ID,
	paint: {
		"line-color": THEME_COLOR,
		"line-width": 2.5,
		"line-dasharray": [3, 2],
		"line-opacity": 0.9,
	},
	layout: { visibility: "none" },
};

const arrowSpec: SymbolLayerSpecification = {
	id: ARROW_ID,
	type: "symbol",
	source: SOURCE_ID,
	layout: {
		"symbol-placement": "line-center",
		"text-field": "▶",
		"text-size": 25,
		"text-rotation-alignment": "map",
		"text-keep-upright": false,
		"text-allow-overlap": true,
		"text-ignore-placement": true,
		visibility: "none",
	},
	paint: {
		"text-color": THEME_COLOR,
		"text-halo-color": "#ffffff",
		"text-halo-width": 2,
	},
};

export class ConnectionsStore {
	visible = false;
	connectionsApi = new AsyncStateStore(networkConnection.getAll);
	createApi = new AsyncStateStore(networkConnection.create);
	removeApi = new AsyncStateStore(networkConnection.remove);
	previewStore: ConnectionPreviewStore;
	private source = new MapBoxSource({ id: SOURCE_ID, spec: sourceSpec });
	private line = new MapBoxLayer({ id: LINE_ID, spec: lineSpec });
	private arrow = new MapBoxLayer({ id: ARROW_ID, spec: arrowSpec });
	private missingLayers = new MissingConnectivityLayers();
	private animationId: number | null = null;
	private dashStep = -1;

	constructor(
		private mapRef: RefObject<MapRef | null>,
		private getLngLat: (id: string) => LngLatTuple | null,
		private popupsStore: MapPopupsStore,
		private onConnectionsChanged?: () => void | Promise<void>,
	) {
		this.previewStore = new ConnectionPreviewStore(mapRef, getLngLat);
		makeAutoObservable(this, {
			connectionsApi: false,
			createApi: false,
			removeApi: false,
			source: false,
			line: false,
			arrow: false,
			missingLayers: false,
			mapRef: false,
			getLngLat: false,
			popupsStore: false,
			onConnectionsChanged: false,
			animationId: false,
			dashStep: false,
		} as any);
	}

	private startAnimation = () => {
		const map = this.mapRef.current?.getMap();
		if (!map) return;
		this.stopAnimation();
		const tick = (timestamp: number) => {
			const nextStep = Math.floor((timestamp / 50) % DASH_SEQUENCE.length);
			if (nextStep !== this.dashStep) {
				this.dashStep = nextStep;
				if (map.getLayer(LINE_ID)) {
					map.setPaintProperty(LINE_ID, "line-dasharray", DASH_SEQUENCE[nextStep]);
				}
			}
			this.animationId = requestAnimationFrame(tick);
		};
		this.animationId = requestAnimationFrame(tick);
	};

	private stopAnimation = () => {
		if (this.animationId != null) {
			cancelAnimationFrame(this.animationId);
			this.animationId = null;
		}
		this.dashStep = -1;
	};

	selectFromMarker = (structureId: Dryp.StructureId | null) => {
		this.previewStore.selectFromMarker(structureId);
	};

	setMissingConnectivity = (structureIds: string[]) => {
		this.missingLayers.setMissingIds(structureIds);
	};

	createConnection = async (toMarkerId: string) => {
		const fromId = this.previewStore.selectedFromMarkerStructureId;
		if (!fromId || String(fromId) === toMarkerId) {
			return;
		}
		const from = this.getLngLat(String(fromId));
		const to = this.getLngLat(toMarkerId);
		if (from && to) {
			this.previewStore.showLoadingLine(from, to);
		}
		try {
			await this.createApi.fetch({
				fromStructureId: fromId,
				toStructureId: toMarkerId as Dryp.StructureId,
				connectionType: "Pipe",
			});
			await this.connectionsApi.fetch({ pageNumber: 0, pageSize: 2000 });
			this.render();
			if (!this.visible) {
				this.visible = true;
				this.line.setVisibility(true);
				this.arrow.setVisibility(true);
				this.missingLayers.setVisible(true);
			}
			this.startAnimation();
			toast.success("Connection created");
			await this.onConnectionsChanged?.();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError?.message || "Failed to create connection. Please try again.");
		} finally {
			this.previewStore.selectFromMarker(null);
		}
	};

	removeConnection = async (connectionId: number) => {
		try {
			await this.removeApi.fetch(connectionId as Dryp.NetworkConnectionId);
			this.popupsStore.closeHoverPopup();
			await this.connectionsApi.fetch({ pageNumber: 0, pageSize: 2000 });
			this.render();
			toast.success("Connection deleted");
			await this.onConnectionsChanged?.();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError?.message || "Failed to delete connection. Please try again.");
		}
	};

	attach = () => {
		const map = this.mapRef.current?.getMap();
		if (!map) return;
		const insert = { beforeId: BELOW_LAYER_ID };
		this.source.attach(map);
		this.line.attach(map, insert);
		this.arrow.attach(map, insert);
		// No beforeId: badges sit above the marker layer.
		this.missingLayers.attach(map);

		this.line
			.onMouseEnter(({ map: hoverMap }) => {
				(hoverMap.getCanvas() as HTMLCanvasElement).style.cursor = "pointer";
			})
			.onMouseLeave(({ map: hoverMap }) => {
				(hoverMap.getCanvas() as HTMLCanvasElement).style.cursor = "";
				this.popupsStore.closeHoverPopup();
			})
			.onMouseMove(({ feature, lng, lat, event, map: hoverMap }) => {
				const point = (event as { point?: { x: number; y: number } }).point;
				if (point && hoverMap.getLayer(BELOW_LAYER_ID)) {
					const markersHere = hoverMap.queryRenderedFeatures(point as any, {
						layers: [BELOW_LAYER_ID],
					});
					if (markersHere.length > 0) {
						this.popupsStore.closeHoverPopup();
						return;
					}
				}
				const rawId = feature?.properties?.id;
				const id = typeof rawId === "number" ? rawId : Number(rawId);
				if (!Number.isFinite(id)) return;
				this.popupsStore.openHoverPopup({
					kind: "connection",
					id: id as Dryp.NetworkConnectionId,
					lng,
					lat,
				});
			});

		if (this.visible) this.startAnimation();
	};

	detach = () => {
		this.stopAnimation();
		this.line.detach();
		this.arrow.detach();
		this.previewStore.selectFromMarker(null);
	};

	toggle = async () => {
		this.visible = !this.visible;
		if (this.visible) {
			await this.connectionsApi.fetchIfEmpty({ pageNumber: 0, pageSize: 2000 });
			this.render();
		}
		this.line.setVisibility(this.visible);
		this.arrow.setVisibility(this.visible);
		this.missingLayers.setVisible(this.visible);
		if (this.visible) this.startAnimation();
		else this.stopAnimation();
	};

	private render() {
		const connections = this.connectionsApi.data?.items ?? [];
		const features: GeoJSON.Feature[] = [];

		for (const connection of connections) {
			const from = this.getLngLat(String(connection.fromStructureId));
			const to = this.getLngLat(String(connection.toStructureId));
			if (!from || !to) continue;
			features.push({
				type: "Feature",
				properties: { id: connection.id },
				geometry: { type: "LineString", coordinates: [from, to] },
			});
		}

		this.source.setGeoJSONData({ type: "FeatureCollection", features });
	}
}
